import {
    useEffect,
    useState
} from "react";

import {
    useParams
} from "react-router";

import {
    VocabularyPack
} from "../features/vocabulary/VocabularyPack.js";

import {
    parseVocabularyLevel
} from "../features/vocabulary/vocabularyLevels.js";

import { useContent } from "../services/content/ContentProvider.js";
import { loadPublishedVocabularyPack } from "../services/content/publicContentReaders.js";

import {
    useI18n
} from "../i18n/I18nProvider.js";

import type {
    Level,
    VocabPack
} from "../types/global.js";

import {
    BackButton
} from "../ui/components/Controls.js";

import {
    EmptyState,
    ErrorState,
    LoadingState
} from "../ui/components/Feedback.js";

import {
    Page
} from "../ui/components/Layout.js";

/**
 * Durable Vocabulary pack route:
 *
 * /vocabulary/:level/:packId
 *
 * Activity navigation remains local to VocabularyPack. This prevents
 * flashcards, stories and quizzes from polluting the application router while
 * still giving each Vocabulary pack a durable URL.
 */
function VocabularyPackPage() {
    const { repository, status: contentStatus } = useContent();
    const {
        level:
            levelParameter,
        packId:
            packIdParameter
    } = useParams();

    const {
        t
    } = useI18n();

    const level =
        parseVocabularyLevel(
            levelParameter
        );

    const packId =
        normalizePackId(
            packIdParameter
        );

    const [
        pack,
        setPack
    ] =
        useState<
            VocabPack | null
        >(
            null
        );

    const [
        loading,
        setLoading
    ] =
        useState(
            true
        );

    const [loadError, setLoadError] = useState(false);

    const [
        retryCount,
        setRetryCount
    ] =
        useState(
            0
        );

    useEffect(
        () => {
            if (
                !level
                || !packId
            ) {
                setPack(
                    null
                );

                setLoading(
                    false
                );

                return;
            }

            if (contentStatus !== "ready" || !repository) {
                setLoading(contentStatus !== "error");
                setLoadError(contentStatus === "error");
                return;
            }
            const contentRepository = repository;

            let active =
                true;

            async function load(
                vocabularyLevel:
                    Level,

                routePackId:
                    string
            ): Promise<void> {
                setLoading(
                    true
                );

                setPack(
                    null
                );

                setLoadError(false);
                try {
                    const loadedPack = await loadPublishedVocabularyPack(contentRepository, vocabularyLevel, routePackId);
                    if (active) setPack(loadedPack);
                } catch {
                    if (active) setLoadError(true);
                } finally {
                    if (active) setLoading(false);
                }
            }

            void load(
                level,
                packId
            );

            return () => {
                active =
                    false;
            };
        },
        [
            level,
            packId,
            retryCount,
            repository,
            contentStatus
        ]
    );

    if (
        !level
        || !packId
    ) {
        return (
            <Page>
                <BackButton
                    fallback="/vocabulary"
                >
                    ←
                    {" "}
                    {t(
                        "common.back"
                    )}
                </BackButton>

                <ErrorState
                    title={
                        t(
                            "error.notFound.title"
                        )
                    }
                    description={
                        t(
                            "vocab.packSoon"
                        )
                    }
                />
            </Page>
        );
    }

    if (loading) {
        return (
            <Page>
                <LoadingState
                    label={
                        t(
                            "common.loading"
                        )
                    }
                />
            </Page>
        );
    }

    if (loadError) {
        return <Page><ErrorState title={t("error.unavailable.title")} description={t("error.unavailable.body")} /></Page>;
    }

    if (!pack) {
        return (
            <Page>
                <BackButton
                    fallback={
                        `/vocabulary/${level}`
                    }
                >
                    ←
                    {" "}
                    {t(
                        "common.back"
                    )}
                </BackButton>

                <EmptyState
                    icon="📖"
                    title={
                        t(
                            "vocab.packSoon"
                        )
                    }
                    action={
                        <button
                            type="button"
                            onClick={() => {
                                setRetryCount(
                                    current =>
                                        current + 1
                                );
                            }}
                            className="
                                text-sm
                                font-bold
                                text-dino-700
                                hover:underline
                                hover:underline-offset-4
                            "
                        >
                            {t(
                                "common.retry"
                            )}
                        </button>
                    }
                />
            </Page>
        );
    }

    return (
        <Page>
            <VocabularyPack
                key={
                    `${level}:${pack.id}`
                }
                pack={
                    pack
                }
            />
        </Page>
    );
}

/**
 * Safely decodes the pack identifier coming from React Router.
 *
 * Malformed percent-encoded URLs are treated as invalid instead of crashing
 * the whole route.
 */
function normalizePackId(
    value:
        string
        | undefined
): string | null {
    if (!value) {
        return null;
    }

    try {
        const decoded =
            decodeURIComponent(
                value
            ).trim();

        return (
            decoded
            || null
        );
    } catch {
        return null;
    }
}

export {
    VocabularyPackPage,
    normalizePackId
};
